/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2020 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Product utilities
 *
 * @file ProductUtils.js
 * @author Prithviraj K
 */

import PathUtils from './PathUtils';
import * as constants from '../constants';

export default class ProductUtils {
  /**
   * Method to get product description
   *
   * @param {Object} data
   * @param {boolean} requestProductModel
   */
  static getProductDescription(data, entitlementLevel, requestProductModel = false) {
    const {
      format,
      model,
      platform,
      subscription: { businessModelCode }
    } = data;
    const {
      BUNDLED_PLATFORM,
      COLLECTIONS,
      CUSTOM_COLLECTIONS
    } = constants;
    const isBundled = platform === BUNDLED_PLATFORM;
    let productModel = '';
    const bookFormat = ProductUtils.getProductTypeFormat(format);

    if (isBundled) {
      productModel = this.getProductModel(businessModelCode, entitlementLevel, isBundled);
    } else {
      productModel = model === CUSTOM_COLLECTIONS
        ? COLLECTIONS
        : this.getProductModel(businessModelCode, entitlementLevel, isBundled);
    }
    const productDescription = `${productModel}, ${bookFormat}`;

    if (requestProductModel) {
      return productModel;
    }
    return productDescription;
  }

  /**
   * Method to get product model
   *
   * @param {string} businessModelCode
   * @param {boolean} isBundled
   */
  static getProductModel(businessModelCode, entitlementLevel, isBundled) {
    const {
      BUNDLE_SINGLE,
      CG_RENEWALS,
      D2L_INTEGRATED_RENEWAL,
      D2L_STANDALONE,
      D2L_STANDALONE_RENEWAL,
      DIRECT_TO_LEARNER,
      DIRECT_TO_LEARNER_RENEWAL,
      STANDALONE_ETEXT,
      INTEGRATED_SMS,
      INTEGRATED_RUMBA,
      DIRECT_TO_CONSUMER,
      DIRECT_TO_CONSUMER_IA,
      INCLUSIVE_ACCESS_DISPLAY_NAME,
      INTEGRATED_SMS_ETEXT,
      INTEGRATED_RUMBA_ETEXT,
      PEARSON_PLUS_ETEXTBOOK,
      PEARSON_PLUS_MULTI,
      INTEGRATED_ETEXT_3L,
      MOJO_US_SINGLE,
      DIGITAL_SAMPLE_MARKETING,
      DIGITAL_SAMPLE_SELF,
      DIGITAL_SAMPLE_REP,
      SAMPLE_FROM_MARKETING,
      SAMPLE_FROM_REP,
      SAMPLE_FROM_SELF,
      DIRECT_TO_CONSUMER_PERPETUAL,
      PEARSON_PLUS_LIFETIME,
      DIRECT_TO_CONSUMER_HEI,
      DIRECT_TO_CONSUMER_HEIUK,
      DIRECT_TO_CONSUMER_HEICA,
      DIRECT_TO_CONSUMER_HEILATAM,
      PEARSON_PLUS_HEIUK,
      PEARSON_PLUS_HEICA,
      PEARSON_PLUS_HEILATAM,
      PEARSON_PLUS_HEI,
      INSTRUCTOR_LED_SAET,
      INSTRUCTOR_LED_SAETIA,
      IL_SAET_IA,
      IL_SAET
    } = constants;
    const tpiValue = PathUtils.getQueryParameterbyName('isTpi');
    const singleSubscriptions = [BUNDLE_SINGLE, MOJO_US_SINGLE];

    switch (businessModelCode) {
      case CG_RENEWALS:
        return D2L_INTEGRATED_RENEWAL;
      case DIRECT_TO_LEARNER_RENEWAL:
        return D2L_STANDALONE_RENEWAL;
      case DIRECT_TO_LEARNER:
        return D2L_STANDALONE;
      case INTEGRATED_SMS:
        return tpiValue && tpiValue.toLowerCase() === 'y' ? INTEGRATED_ETEXT_3L : INTEGRATED_SMS_ETEXT;
      case INTEGRATED_RUMBA:
        return INTEGRATED_RUMBA_ETEXT;
      case DIRECT_TO_CONSUMER:
        return singleSubscriptions.includes(entitlementLevel) ? PEARSON_PLUS_ETEXTBOOK : PEARSON_PLUS_MULTI;
      case DIRECT_TO_CONSUMER_IA:
        return INCLUSIVE_ACCESS_DISPLAY_NAME;
      case DIGITAL_SAMPLE_REP:
        return SAMPLE_FROM_REP;
      case DIGITAL_SAMPLE_SELF:
        return SAMPLE_FROM_SELF;
      case DIGITAL_SAMPLE_MARKETING:
        return SAMPLE_FROM_MARKETING;
      case DIRECT_TO_CONSUMER_PERPETUAL:
        return PEARSON_PLUS_LIFETIME;
      case DIRECT_TO_CONSUMER_HEIUK:
        return PEARSON_PLUS_HEIUK;
      case DIRECT_TO_CONSUMER_HEICA:
        return PEARSON_PLUS_HEICA;
      case DIRECT_TO_CONSUMER_HEILATAM:
        return PEARSON_PLUS_HEILATAM;
      case DIRECT_TO_CONSUMER_HEI:
        return PEARSON_PLUS_HEI;
      case INSTRUCTOR_LED_SAET:
        return IL_SAET;
      case INSTRUCTOR_LED_SAETIA:
        return IL_SAET_IA;
      default:
        return isBundled ? INTEGRATED_ETEXT_3L : STANDALONE_ETEXT;
    }
  }

  /**
   * Method to get customer-facing product label
   *
   * @param {string} businessModelCode
   * @returns {string}
   */
  static getProductLabel(businessModelCode) {
    const {
      DIRECT_TO_CONSUMER,
      DIRECT_TO_CONSUMER_PERPETUAL,
      DIRECT_TO_CONSUMER_HEICA,
      DIRECT_TO_CONSUMER_HEIUK,
      DIRECT_TO_CONSUMER_HEI,
      DIRECT_TO_CONSUMER_HEILATAM,
      INSTRUCTOR_LED_SAETIA,
      INSTRUCTOR_LED_SAET,
      INSTRUCTOR_LED_SAET_K12,
      INSTRUCTOR_LED_SAET_IA_K12,
      DIGITAL_SAMPLE_SELF,
      DIGITAL_SAMPLE_REP,
      INTEGRATED_SMS,
      CG_RENEWALS,
      PEARSON_PLUS_ETEXTBOOK,
      PEARSON_PLUS_LIFETIME_ACCESS,
      PEARSON_ETEXT_TITLE,
      INSTRUCTOR_LED_ETEXTBOOK_COURSE,
      K12_INSTRUCTOR_LED_ETEXTBOOK_COURSE,
      DIGITAL_SAMPLE_SELF_REQUESTED,
      DIGITAL_SAMPLE_FROM_SALES_REP,
      ETEXTBOOK_FOR_MLM_COURSE,
      ETEXTBOOK_RENEWAL_FROM_MLM_COURSE
    } = constants;

    switch (businessModelCode) {
      case DIRECT_TO_CONSUMER:
        return PEARSON_PLUS_ETEXTBOOK;
      case DIRECT_TO_CONSUMER_PERPETUAL:
        return PEARSON_PLUS_LIFETIME_ACCESS;
      case DIRECT_TO_CONSUMER_HEICA:
      case DIRECT_TO_CONSUMER_HEIUK:
      case DIRECT_TO_CONSUMER_HEI:
      case DIRECT_TO_CONSUMER_HEILATAM:
        return PEARSON_ETEXT_TITLE;
      case INSTRUCTOR_LED_SAETIA:
      case INSTRUCTOR_LED_SAET:
        return INSTRUCTOR_LED_ETEXTBOOK_COURSE;
      case INSTRUCTOR_LED_SAET_K12:
      case INSTRUCTOR_LED_SAET_IA_K12:
        return K12_INSTRUCTOR_LED_ETEXTBOOK_COURSE;
      case DIGITAL_SAMPLE_SELF:
        return DIGITAL_SAMPLE_SELF_REQUESTED;
      case DIGITAL_SAMPLE_REP:
        return DIGITAL_SAMPLE_FROM_SALES_REP;
      case INTEGRATED_SMS:
        return ETEXTBOOK_FOR_MLM_COURSE;
      case CG_RENEWALS:
        return ETEXTBOOK_RENEWAL_FROM_MLM_COURSE;
      default:
        return '';
    }
  }

  /**
   * Method to get legacyBookId for user data migration
   *
   * @param {string} businessModelCode
   * @param {object} legacyIds : {bookId, productId}
   */
  static getlegacyBookId(businessModelCode, legacyIds) {
    const { bookId, productId } = legacyIds;
    let legacyBookId = productId;
    const {
      CG_RENEWALS,
      INTEGRATED_SMS
    } = constants;
    const MLM_BMC_LIST = [CG_RENEWALS, INTEGRATED_SMS];
    if (MLM_BMC_LIST.includes(businessModelCode)) {
      legacyBookId = bookId;
    }
    return legacyBookId;
  }

    /**
   * To get product type format
   *
   * @param {*} bookType
   * @returns
   */
    static getProductTypeFormat = (bookType) => {
      const {
        BRONTE_VIEWER,
        BRONTE_EPUB,
        CONTENT_TYPE_EPUB,
        CONTENT_TYPE_BV_BRONTE,
        CONTENT_TYPE_BV_VIEWER
      } = constants;
      const renderBookType = bookType && bookType.toUpperCase();
      let format = bookType;

      switch (renderBookType) {
        case CONTENT_TYPE_BV_BRONTE:
        case CONTENT_TYPE_BV_VIEWER:
          format = BRONTE_VIEWER;
          break;
        case CONTENT_TYPE_EPUB:
          format = BRONTE_EPUB;
          break;
        default:
          format = bookType;
          break;
      }

      return format;
    }

    /**
     * Is bronte viewer book
     *
     * @param {*} bookType
     * @returns
     */
    static isBVBronteBook = (bookType) => {
      const renderBookType = bookType && bookType.toUpperCase();
      return ((renderBookType === constants.CONTENT_TYPE_BV_BRONTE) || (renderBookType === constants.CONTENT_TYPE_BV_VIEWER));
    }
}
